import { useRef } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { UseFormReturn } from 'react-hook-form';

import { useConfirmation } from '@/components/base/confirmation';
import { ChannelFormState, JobPostingForm } from '../../types';
import { SelectedSection } from '../side-nav';

// Snapshot captured when entering the common section
type CommonSnapshot = {
  title: string;
  description: string;
  stateCode: string | undefined;
};

// Snapshot captured when entering a contract (Vonq) section
type ContractSnapshot = {
  contractId: string;
  vonqValues: Record<string, string | string[]>;
};

interface UseSectionSnapshotOptions {
  openingExternalId: string;
  hookForm: UseFormReturn<JobPostingForm>;
  selectedSection: SelectedSection;
  /** The channel form state for the currently selected non-common section. */
  selectedChannel: ChannelFormState | null;
  /** All channel form states, used to resolve the contractId when snapshotting. */
  channels: Record<string, ChannelFormState> | undefined;
  collapseOpening: () => void;
}

/**
 * Encapsulates per-section snapshot management and the Cancel flow for the
 * expanded job-detail card.
 *
 * - `snapshotSection` — call on entry to a new section and after a successful
 *   Save so the cancel baseline stays in sync with what was last persisted.
 * - `handleCancelClick` — reverts the current section to its snapshot. Shows
 *   a confirmation dialog only when there are unsaved changes.
 */
export function useSectionSnapshot({
  openingExternalId,
  hookForm,
  selectedSection,
  selectedChannel,
  channels,
  collapseOpening,
}: UseSectionSnapshotOptions) {
  const intl = useIntl();
  const { confirm } = useConfirmation();
  const { getValues, setValue } = hookForm;

  // Per-section entry snapshots — keyed by section name ('common' or channelKey).
  // Initialised with the common section's values at mount time so Cancel works
  // without requiring the user to switch sections first.
  const sectionSnapshotsRef = useRef<
    Record<string, CommonSnapshot | ContractSnapshot>
  >({
    common: {
      title: getValues(`jobDetails.${openingExternalId}.title`),
      description: getValues(`jobDetails.${openingExternalId}.description`),
      stateCode: getValues(`jobDetails.${openingExternalId}.stateCode`),
    },
  });

  /** Capture the current values of a section as the baseline for Cancel. */
  const snapshotSection = (section: SelectedSection) => {
    if (section === 'common') {
      sectionSnapshotsRef.current['common'] = {
        title: getValues(`jobDetails.${openingExternalId}.title`),
        description: getValues(`jobDetails.${openingExternalId}.description`),
        stateCode: getValues(`jobDetails.${openingExternalId}.stateCode`),
      };
    } else {
      const contractId = channels?.[section]?.vonqDetails?.contractId;
      if (contractId) {
        const vonqReqs =
          getValues(
            `jobDetails.${openingExternalId}.vonqPostingRequirements`,
          ) ?? {};
        sectionSnapshotsRef.current[section] = {
          contractId,
          vonqValues: { ...(vonqReqs[contractId] ?? {}) },
        };
      }
    }
  };

  /** True when the live form values differ from the section's entry snapshot. */
  const hasSectionChanged = (): boolean => {
    const snapshot = sectionSnapshotsRef.current[selectedSection];
    if (!snapshot) return false;

    if (selectedSection === 'common') {
      const s = snapshot as CommonSnapshot;
      // Normalise empty string and undefined to the same value so that clearing
      // stateCode back to blank doesn't trigger a false "Discard changes?" dialog.
      const normalise = (v: string | undefined) => v || undefined;
      return (
        getValues(`jobDetails.${openingExternalId}.title`) !== s.title ||
        getValues(`jobDetails.${openingExternalId}.description`) !==
          s.description ||
        normalise(getValues(`jobDetails.${openingExternalId}.stateCode`)) !==
          normalise(s.stateCode)
      );
    }

    const s = snapshot as ContractSnapshot;
    if (!s.contractId) return false;
    const currentValues =
      getValues(`jobDetails.${openingExternalId}.vonqPostingRequirements`)?.[
        s.contractId
      ] ?? {};
    return JSON.stringify(currentValues) !== JSON.stringify(s.vonqValues);
  };

  /**
   * Revert the current section to its last snapshot and collapse the card.
   * A confirmation dialog is shown only when there are unsaved changes.
   */
  const handleCancelClick = async () => {
    if (hasSectionChanged()) {
      const sectionName =
        selectedSection === 'common'
          ? intl.formatMessage({
              defaultMessage: 'Common fields',
              description: 'Section name used in the discard-changes dialog',
            })
          : (selectedChannel?.displayTitle ?? selectedSection);

      const confirmed = await confirm({
        title: intl.formatMessage({
          defaultMessage: 'Discard changes?',
          description:
            'Title of the dialog asking the user to confirm discarding unsaved job-detail edits',
        }),
        description: (
          <FormattedMessage
            defaultMessage="Your unsaved changes to {section} will be lost."
            description="Body of the dialog asking the user to confirm discarding unsaved job-detail edits"
            values={{ section: <strong>{sectionName}</strong> }}
          />
        ),
        confirmLabel: intl.formatMessage({
          defaultMessage: 'Discard',
          description:
            'Confirm button label in the discard-changes dialog — proceeds with discarding',
        }),
        cancelLabel: intl.formatMessage({
          defaultMessage: 'Keep editing',
          description:
            'Cancel button label in the discard-changes dialog — returns to the form',
        }),
        confirmVariant: 'destructive',
      });
      if (!confirmed) return;
    }

    const snapshot = sectionSnapshotsRef.current[selectedSection];

    if (selectedSection === 'common') {
      const s = snapshot as CommonSnapshot | undefined;
      setValue(`jobDetails.${openingExternalId}.title`, s?.title ?? '', {
        shouldValidate: true,
      });
      setValue(
        `jobDetails.${openingExternalId}.description`,
        s?.description ?? '',
        { shouldValidate: true },
      );
      setValue(`jobDetails.${openingExternalId}.stateCode`, s?.stateCode, {
        shouldValidate: true,
      });
    } else {
      const s = snapshot as ContractSnapshot | undefined;
      if (s?.contractId) {
        setValue(
          `jobDetails.${openingExternalId}.vonqPostingRequirements.${s.contractId}`,
          s.vonqValues,
          { shouldValidate: true },
        );
      }
    }

    collapseOpening();
  };

  return { snapshotSection, handleCancelClick };
}
